import React from 'react';
import {
  Combobox as HeadlessCombobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react';
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { ErrorMessage } from '@oysterjs/ui/fieldset';
import { Spinner } from '@oysterjs/ui/spinner';

export type Recipient = {
  email: string;
  /** Display name, when one is known; the chip falls back to the address. */
  name?: string;
};

export type RecipientSuggestion = Recipient & {
  /** Secondary line in the dropdown, e.g. the account the contact belongs to. */
  description?: string;
};

/** RFC 5322 atext, plus the non-ASCII letters the server's parser accepts. */
const ATOM = "[a-z0-9!#$%&'*+/=?^_`{|}~\\u0080-\\uFFFF-]+";
const DOMAIN = '(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z]{2,}';

/**
 * Lets the sender fix an address before pressing Send; the server checks it
 * again. A dot-atom local part refuses spaces, brackets, quotes and stray dots,
 * and the domain must end in a dotted label of letters.
 */
const EMAIL_SHAPE = new RegExp(`^${ATOM}(?:\\.${ATOM})*@${DOMAIN}$`, 'i');

export const isEmailShaped = (email: string): boolean => EMAIL_SHAPE.test(email.trim());

/** Matches the `Display Name <addr@example.com>` form that mail clients copy. */
const DISPLAY_NAME_ADDRESS = /^"?([^"<>]*?)"?\s*<\s*([^<>\s]+)\s*>$/;

const SEPARATORS = /[,;\n\r]+/;

/**
 * The fragment a lookup should run against. Typing `ada@x.com, sh` means the
 * user is searching for `sh`, not for the whole field; the separator normally
 * commits a chip before this matters, but a paste or an IME can still leave
 * one in the input.
 */
const searchFragment = (text: string): string => (text.split(SEPARATORS).pop() ?? '').trim();

/**
 * Splits free text — typed or pasted — into recipients. Handles the three
 * shapes a paste out of a mail client can take: comma or semicolon separated
 * lists, one address per line, and `Name <addr>` pairs. Bare addresses
 * separated only by spaces are split too, which is safe because an address
 * cannot itself contain one.
 */
export const parseRecipients = (text: string): Recipient[] =>
  text
    .split(SEPARATORS)
    .flatMap((token) => {
      const trimmed = token.trim();
      if (!trimmed) {
        return [];
      }

      const named = trimmed.match(DISPLAY_NAME_ADDRESS);
      if (named) {
        const name = named[1].trim();
        return [{ email: named[2].trim(), ...(name ? { name } : {}) }];
      }

      return trimmed
        .split(/\s+/)
        .filter(Boolean)
        .map((email) => ({ email }));
    })
    .filter((recipient) => recipient.email.length > 0);

const sameAddress = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Keeps the first occurrence of each address, comparing case-insensitively. */
const dedupe = (recipients: Recipient[]): Recipient[] =>
  recipients.filter(
    (recipient, index) =>
      recipients.findIndex((other) => sameAddress(other.email, recipient.email)) === index
  );

/** Debounces a changing value so a keystroke does not cost a lookup. */
const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

type Option = {
  recipient: Recipient;
  label: string;
  description?: string;
};

export type RecipientInputProps = {
  value: Recipient[];
  onChange: (recipients: Recipient[]) => void;
  /**
   * Contact lookup. Omitting it leaves a plain free-entry field, which is the
   * fallback whenever a mailbox has no address book to search.
   */
  onSearch?: (query: string) => Promise<RecipientSuggestion[]>;
  searchDebounceMs?: number;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Caller-supplied message — a send failure, say. It replaces the built-in
   * malformed-address message so the field never shows two errors at once.
   */
  error?: string;
  autoFocus?: boolean;
  id?: string;
  'aria-label'?: string;
  /**
   * Drops the box around the field, for a mail-client header where the row's
   * own hairline and the caret are the affordance. Chips, lookup and keyboard
   * handling are untouched.
   */
  bare?: boolean;
};

/**
 * A To/CC/BCC field: removable chips for the chosen addresses, an inline
 * search over an optional contact directory, and free entry for the addresses
 * that are in no directory at all.
 *
 * A sibling of `MultiCombobox` rather than an extension of it: that component
 * materialises its options up front and can only ever return one of them, and
 * both assumptions are load-bearing for its callers.
 *
 * Free entry rides on Headless UI's combobox rather than a parallel keyboard
 * path: whatever the user has typed is offered as the first option, so Enter
 * commits it through the same selection machinery that commits a suggestion.
 */
export const RecipientInput = ({
  value,
  onChange,
  onSearch,
  searchDebounceMs = 200,
  placeholder,
  disabled,
  error,
  autoFocus,
  id,
  'aria-label': ariaLabel,
  bare
}: RecipientInputProps) => {
  // Mirrors MultiCombobox: displayValue reads the ref so the input clears in
  // the same render as the selection instead of flashing the selected label.
  const queryRef = React.useRef('');
  const [query, setQueryState] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<RecipientSuggestion[]>([]);
  const [searchPending, setSearchPending] = React.useState(false);
  const controlRef = React.useRef<HTMLSpanElement>(null);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const setQuery = React.useCallback((next: string) => {
    queryRef.current = next;
    setQueryState(next);
  }, []);

  /**
   * Headless UI owns the input's DOM value and only resyncs it from
   * displayValue when a selection changes, so a commit driven from the
   * keyboard has to blank the field itself or the next keystroke appends to
   * the address just chipped.
   */
  const clearQuery = React.useCallback(() => {
    setQuery('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [setQuery]);

  const fragment = searchFragment(query);
  const debouncedFragment = useDebouncedValue(fragment, searchDebounceMs);

  // Held in a ref so a caller passing an inline arrow does not restart the
  // search on every render.
  const onSearchRef = React.useRef(onSearch);
  React.useEffect(() => {
    onSearchRef.current = onSearch;
  });

  React.useEffect(() => {
    const search = onSearchRef.current;
    const trimmed = debouncedFragment;
    if (!search || !trimmed) {
      setSuggestions([]);
      setSearchPending(false);
      return;
    }

    // Guards against a slow earlier lookup landing after a newer one.
    let current = true;
    setSearchPending(true);
    search(trimmed)
      .then((results) => {
        if (current) {
          setSuggestions(results);
        }
      })
      .catch(() => {
        // A directory that is down must still leave the field typeable.
        if (current) {
          setSuggestions([]);
        }
      })
      .finally(() => {
        if (current) {
          setSearchPending(false);
        }
      });

    return () => {
      current = false;
    };
  }, [debouncedFragment]);

  const commit = React.useCallback(
    (recipients: Recipient[]) => {
      clearQuery();
      if (recipients.length > 0) {
        onChange(dedupe([...value, ...recipients]));
      }
    },
    [clearQuery, onChange, value]
  );

  const commitQuery = React.useCallback(() => {
    commit(parseRecipients(query));
  }, [commit, query]);

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  const parsed = React.useMemo(() => parseRecipients(query), [query]);
  const typed = parsed[parsed.length - 1];

  const options = React.useMemo((): Option[] => {
    const fromDirectory = suggestions.map((suggestion) => ({
      recipient: { email: suggestion.email, ...(suggestion.name ? { name: suggestion.name } : {}) },
      label: suggestion.name ?? suggestion.email,
      description: suggestion.description ?? (suggestion.name ? suggestion.email : undefined)
    }));

    // The free-entry row is dropped once the address is already a chip or is
    // about to be offered by the directory, so Enter can never toggle an
    // existing chip back off.
    const alreadyOffered =
      !typed ||
      value.some((recipient) => sameAddress(recipient.email, typed.email)) ||
      suggestions.some((suggestion) => sameAddress(suggestion.email, typed.email));

    if (alreadyOffered) {
      return fromDirectory;
    }

    return [
      {
        recipient: typed,
        label: typed.name ? `${typed.name} <${typed.email}>` : typed.email,
        description: isEmailShaped(typed.email)
          ? 'Add this address'
          : 'Add anyway — this does not look like an email address'
      },
      ...fromDirectory
    ];
  }, [suggestions, typed, value]);

  const malformed = value.filter((recipient) => !isEmailShaped(recipient.email));
  const message =
    error ??
    (malformed.length === 1
      ? `${malformed[0].email} does not look like an email address`
      : malformed.length > 1
        ? `${malformed.length} addresses do not look like email addresses`
        : undefined);

  // The spinner covers the debounce window too, so a keystroke gives immediate
  // feedback rather than going quiet until the request starts.
  const loading = !!onSearch && !!fragment && (searchPending || fragment !== debouncedFragment);

  // Headless UI's ComboboxInput runs these handlers before its own and honours
  // preventDefault, which is how Backspace and Enter are taken over below
  // without the combobox also acting on them.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && query === '' && value.length > 0) {
      event.preventDefault();
      removeAt(value.length - 1);
      return;
    }

    if (event.key === ',' || event.key === ';') {
      event.preventDefault();
      commitQuery();
      return;
    }

    // A single address is committed by selecting the free-entry option, so
    // Enter is only taken over when the input holds a list the option cannot
    // represent — otherwise the leading addresses would be dropped.
    if (event.key === 'Enter' && parsed.length > 1) {
      event.preventDefault();
      commitQuery();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Focus moving inside the control — onto a chip's remove button, or onto
    // a suggestion the user is clicking — is not the user leaving the field,
    // and committing there would add a chip they never asked for.
    if (controlRef.current?.contains(event.relatedTarget)) {
      return;
    }
    commitQuery();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text');
    const recipients = parseRecipients(pasted);

    // A single address is left in the input so the user can still edit it
    // before committing; only a list is worth turning into chips outright.
    if (recipients.length < 2) {
      return;
    }

    event.preventDefault();
    commit(recipients);
  };

  return (
    <>
      <HeadlessCombobox
        as="div"
        data-slot="control"
        value={value}
        onChange={(next: Recipient[]) => {
          clearQuery();
          onChange(dedupe(next));
        }}
        by={(a: Recipient | null, b: Recipient | null) =>
          !!a && !!b && sameAddress(a.email, b.email)
        }
        multiple
        immediate
        disabled={disabled}
        data-invalid={message ? true : undefined}
      >
        <span
          className={clsx([
            'relative block w-full has-[[data-disabled]]:opacity-50',
            !bare && [
              'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white dark:before:bg-neutral-800 before:shadow',
              'dark:before:hidden',
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:focus-within:ring-2 after:focus-within:ring-primary-500',
              'before:has-[[data-disabled]]:bg-slate-950/5 dark:before:has-[[data-disabled]]:bg-white/10 before:has-[[data-disabled]]:shadow-none',
              'before:has-[[data-invalid]]:shadow-red-500/10'
            ]
          ])}
          data-invalid={message ? true : undefined}
          ref={controlRef}
        >
          <div
            className={clsx([
              'relative flex flex-wrap items-center gap-x-1.5 gap-y-1 w-full appearance-none bg-transparent',
              bare
                ? // Height matched to the line box of a bare text control so
                  // chips sit on the same baseline as the label beside them,
                  // and a standing gap on the right so the search spinner
                  // never lands on what is being typed.
                  'min-h-6 pr-6'
                : [
                    'rounded-lg',
                    'py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
                    'min-h-11 sm:min-h-9',
                    'pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing[9])-1px)] sm:pl-[calc(theme(spacing[3])-1px)]',
                    'border border-slate-950/10 dark:border-white/10',
                    'dark:bg-white/5',
                    message && 'border-red-500'
                  ]
            ])}
          >
            {value.map((recipient, index) => {
              const invalid = !isEmailShaped(recipient.email);
              // A bare address, and a directory that names a contact after
              // their own address, both arrive as a name equal to the email.
              const name =
                recipient.name && !sameAddress(recipient.name, recipient.email)
                  ? recipient.name
                  : undefined;
              const label = name ?? recipient.email;
              return (
                <span
                  key={`${recipient.email.toLowerCase()}-${index}`}
                  // Recoverable text for the chip that had to truncate.
                  title={name ? `${name} <${recipient.email}>` : undefined}
                  data-invalid={invalid ? true : undefined}
                  className={clsx(
                    'inline-flex max-w-full items-center gap-1 self-center shrink-0 rounded px-2 text-xs py-0.5',
                    invalid
                      ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-800'
                      : 'bg-primary-100 dark:bg-primary-500/20'
                  )}
                >
                  {invalid && (
                    <ExclamationCircleIcon className="size-3 shrink-0" aria-hidden="true" />
                  )}
                  <span className="truncate">{label}</span>
                  {/* Beside the name rather than under it: contacts who share a
                      name have to be told apart without hovering. */}
                  {name && <span className="truncate opacity-60">{recipient.email}</span>}
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    className={clsx(
                      'shrink-0 focus:outline-none',
                      invalid
                        ? 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                        : 'text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300'
                    )}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      removeAt(index);
                    }}
                  >
                    <XMarkIcon className="size-3" />
                  </button>
                </span>
              );
            })}

            <ComboboxInput
              ref={inputRef}
              id={id}
              aria-label={ariaLabel}
              autoFocus={autoFocus}
              autoComplete="off"
              spellCheck={false}
              className={clsx([
                'flex-1 self-center bg-transparent border-0 p-0',
                // A narrow rail leaves little room beside a chip, so the bare
                // field settles for a shorter unwrapped typing width.
                bare ? 'min-w-[6rem]' : 'min-w-[8rem]',
                'text-base/6 text-slate-950 placeholder:text-slate-500 dark:placeholder:text-slate-400 sm:text-sm/6 dark:text-white',
                'focus:outline-none focus:ring-0',
                'dark:[color-scheme:dark]'
              ])}
              displayValue={() => queryRef.current}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onPaste={handlePaste}
              placeholder={value.length === 0 ? placeholder : undefined}
              data-invalid={message ? true : undefined}
            />
          </div>

          {loading && (
            <span
              className={clsx('absolute inset-y-0 right-0 flex items-center', !bare && 'pr-3')}
              role="status"
              aria-label="Searching contacts"
            >
              <Spinner size="size-4" color="neutral" />
            </span>
          )}

          {options.length > 0 && (
            <ComboboxOptions
              /*
               * An open panel in modal mode marks the rest of the page inert,
               * which would put the chips' own remove buttons out of reach
               * mid-search.
               */
              modal={false}
              /*
               * Anchored into a portal, unlike `MultiCombobox`, which renders
               * in place: this field can sit inside a scrolling panel, where a
               * panel rendered in place is cropped at that panel's edge. The
               * width follows the typing area rather than the whole control,
               * with a floor under it so a field crowded with chips still gets
               * a readable list.
               */
              anchor={{ to: 'bottom start', gap: 4 }}
              // An anchored panel is given its height by the positioner, as an
              // inline style a max-h class cannot outrank, so the ceiling is
              // set through the variable the positioner reads.
              className="z-[200] w-[max(var(--input-width),18rem)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-neutral-800 dark:ring-white/10 [--anchor-max-height:16rem]"
            >
              {options.map((option) => (
                <ComboboxOption
                  key={option.recipient.email.toLowerCase()}
                  value={option.recipient}
                  className={clsx(
                    'group relative cursor-default select-none py-2 pl-3 pr-9',
                    'text-neutral-900 dark:text-white',
                    'data-[focus]:bg-primary-500 data-[focus]:text-white'
                  )}
                >
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block text-xs text-neutral-500 group-data-[focus]:text-neutral-100 dark:text-neutral-400">
                      {option.description}
                    </span>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </span>
      </HeadlessCombobox>
      {!!message && <ErrorMessage>{message}</ErrorMessage>}
    </>
  );
};
